import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import YouTubeAudio from './YouTubeAudio';

const MusicCtx = createContext(null);

export const MusicPlayerProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ autoplay: false, defaultVolume: 20, startMuted: true, shuffle: false });
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(20);
  const [muted, setMuted] = useState(true);
  const [ready, setReady] = useState(false);
  const ytRef = useRef(null);
  const audioCtxRef = useRef(null);

  const currentItem = items.length ? items[current % items.length] : null;

  useEffect(() => {
    let active = true;
    let cleanupDeferredStart = null;
    const isTouchMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px), (pointer: coarse)').matches;
    const fetchMusic = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8000' : 'https://crapule-bxl-production.up.railway.app')}/api/music`;
        const res = await fetch(url);
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        if (!active) return;
        const its = Array.isArray(data.items) ? data.items : [];
        setItems(its);
        const opts = data.options || {};
        const defVol = typeof opts.defaultVolume === 'number' ? opts.defaultVolume : 20;
        setOptions({
          autoplay: !!opts.autoplay,
          defaultVolume: defVol,
          startMuted: !!opts.startMuted,
          shuffle: !!opts.shuffle,
        });
        setVolume(defVol);
        setMuted(!!opts.startMuted);
        if (opts.shuffle && its.length > 1) {
          const idx = Math.floor(Math.random() * its.length);
          setCurrent(idx);
        } else {
          setCurrent(0);
        }
        if (opts.autoplay) setPlaying(true);
      } catch {
        // ignore
      }
    };

    const startDeferredFetch = () => {
      if (!active) return;
      fetchMusic();
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(startDeferredFetch, { timeout: isTouchMobile ? 2800 : 1500 });
      cleanupDeferredStart = () => window.cancelIdleCallback(idleId);
    } else {
      const timeoutId = window.setTimeout(startDeferredFetch, isTouchMobile ? 1200 : 350);
      cleanupDeferredStart = () => window.clearTimeout(timeoutId);
    }

    return () => {
      active = false;
      cleanupDeferredStart?.();
    };
  }, []);

  const next = () => {
    if (items.length === 0) return;
    setCurrent((i) => (i + 1) % items.length);
  };
  const prev = () => {
    if (items.length === 0) return;
    setCurrent((i) => (i - 1 + items.length) % items.length);
  };
  const selectIndex = (i) => { if (i >= 0 && i < items.length) setCurrent(i); };
  const ensureAudioCtx = () => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch {}
    }
    return audioCtxRef.current;
  };
  const playUXClick = (type = 'play') => {
    const ctx = ensureAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    if (type === 'play') {
      // DJ start cue: short highpass noise + click
      const bufferSize = 0.2 * ctx.sampleRate;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1);
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 1800;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, now);
      g.gain.exponentialRampToValueAtTime(0.12, now + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
      noise.connect(hp).connect(g).connect(ctx.destination);
      noise.start(now);
      noise.stop(now + 0.1);
    } else {
      // DJ stop brake: brief pitch-down sine with quick decay
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200, now);
      osc.frequency.exponentialRampToValueAtTime(220, now + 0.12);
      g.gain.setValueAtTime(0.15, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc.connect(g).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.13);
    }
  };
  const togglePlay = () => {
    setPlaying(p => {
      const next = !p;
      playUXClick(next ? 'play' : 'stop');
      return next;
    });
  };
  const toggleMute = () => setMuted(m => !m);
  const setVol = (v) => setVolume(Math.max(0, Math.min(100, v)));
  const seekBy = (ds) => {
    if (ytRef.current && typeof ytRef.current.seekBy === 'function') {
      ytRef.current.seekBy(ds);
    }
  };
  const seekTo = (s) => {
    if (ytRef.current && typeof ytRef.current.seekTo === 'function') {
      ytRef.current.seekTo(s);
    }
  };
  const getCurrentTime = () => {
    if (ytRef.current && typeof ytRef.current.getCurrentTime === 'function') {
      return ytRef.current.getCurrentTime();
    }
    return 0;
  };

  const value = useMemo(() => ({
    items, options, current, currentItem,
    playing, volume, muted, ready,
    next, prev, selectIndex, togglePlay, toggleMute, setVol, setPlaying, setMuted,
    seekBy, seekTo, getCurrentTime,
  }), [items, options, current, currentItem, playing, volume, muted, ready]);

  return (
    <MusicCtx.Provider value={value}>
      {children}
      {currentItem?.youtubeId ? (
        <YouTubeAudio
          videoId={currentItem.youtubeId}
          playing={playing}
          volume={volume}
          muted={muted}
          onEnded={next}
          onReady={() => setReady(true)}
          onPlayer={(api) => { ytRef.current = api; }}
        />
      ) : null}
    </MusicCtx.Provider>
  );
};

export const useMusic = () => {
  const ctx = useContext(MusicCtx);
  if (!ctx) throw new Error('useMusic must be used within MusicPlayerProvider');
  return ctx;
};

export default MusicPlayerProvider;
